import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiShield, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiInfo, FiSearch, FiDownload, FiX, FiChevronsLeft, FiChevronsRight,
  FiFilter,
} from 'react-icons/fi';
import { getAdminAuditLogs, exportAdminAuditLogsCsv } from '../../api/admin';
import type { AdminAuditLogRow } from '../../api/admin';

/* ── Color coding par préfixe d'action ───────────────────────────────────── */
function actionBadge(action: string) {
  const a = action.toUpperCase();
  if (a.startsWith('DELETE') || a.startsWith('DEPROVISION') || a.startsWith('DISABLE'))
    return 'bg-red-50 text-red-700 border border-red-200';
  if (a.startsWith('CREATE') || a.startsWith('PROVISION') || a.startsWith('ENABLE') || a.startsWith('ACTIVATE'))
    return 'bg-green-50 text-green-700 border border-green-200';
  if (a.startsWith('UPDATE') || a.startsWith('PATCH'))
    return 'bg-orange-50 text-orange-700 border border-orange-200';
  if (a.startsWith('LOGIN') || a.startsWith('AUTH') || a.startsWith('LOGOUT'))
    return 'bg-blue-50 text-blue-700 border border-blue-200';
  if (a.startsWith('TEST') || a.startsWith('SYNC'))
    return 'bg-purple-50 text-purple-700 border border-purple-200';
  if (a.startsWith('BOOTSTRAP') || a.startsWith('IMPORT'))
    return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
  return 'bg-gray-100 text-gray-700 border border-gray-200';
}

/* ── Composant principal ──────────────────────────────────────────────────── */
const LIMIT = 25;

const AdminAuditLogs: React.FC = () => {
  const [logs,     setLogs]     = useState<AdminAuditLogRow[]>([]);
  const [total,    setTotal]    = useState(0);
  const [actions,  setActions]  = useState<string[]>([]);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filtres
  const [search,    setSearch]    = useState('');
  const [action,    setAction]    = useState('');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [action, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { page, limit: LIMIT };
      if (debouncedSearch) params.search    = debouncedSearch;
      if (action)          params.action    = action;
      if (dateFrom)        params.date_from = dateFrom;
      if (dateTo)          params.date_to   = dateTo + 'T23:59:59';
      const data = await getAdminAuditLogs(params);
      setLogs(data.logs);
      setTotal(data.total);
      if (data.actions?.length) setActions(data.actions);
    } catch {
      setError("Impossible de charger les logs d'audit");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, action, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (debouncedSearch) params.search    = debouncedSearch;
      if (action)          params.action    = action;
      if (dateFrom)        params.date_from = dateFrom;
      if (dateTo)          params.date_to   = dateTo + 'T23:59:59';
      const blob = await exportAdminAuditLogsCsv(params);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
    finally { setExporting(false); }
  };

  const resetFilters = () => {
    setSearch(''); setAction(''); setDateFrom(''); setDateTo('');
    setPage(1);
  };

  const hasFilters = !!(search || action || dateFrom || dateTo);
  const totalPages = Math.ceil(total / LIMIT);
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to   = Math.min(page * LIMIT, total);

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiShield className="text-red-600" /> Audit Logs
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {total > 0 ? `${total} entrée${total > 1 ? 's' : ''}${hasFilters ? ' (filtrées)' : ''}` : 'Aucune entrée'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} disabled={exporting || total === 0}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-50">
            {exporting ? <FiRefreshCw size={14} className="animate-spin" /> : <FiDownload size={14} />}
            Exporter CSV
          </button>
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Admin, action, IP, cible…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          {/* Action filter */}
          <div className="flex items-center gap-1.5">
            <FiFilter size={13} className="text-gray-400" />
            <select value={action} onChange={e => setAction(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white">
              <option value="">Toutes les actions</option>
              {actions.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <span className="text-gray-400 text-sm">→</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          {/* Reset */}
          {hasFilters && (
            <button onClick={resetFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
              <FiX size={13} /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {!loading && total === 0 && !error && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FiShield size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {hasFilters ? 'Aucun log ne correspond aux filtres' : "Aucun log d'audit pour l'instant"}
          </p>
          {hasFilters && (
            <button onClick={resetFilters} className="mt-2 text-sm text-red-600 hover:underline">
              Effacer les filtres
            </button>
          )}
        </div>
      )}

      {(logs.length > 0 || loading) && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cible</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">IP</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      <FiRefreshCw className="animate-spin inline mr-2" size={14} /> Chargement…
                    </td>
                  </tr>
                ) : logs.map(log => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        <div>{new Date(log.created_at).toLocaleDateString('fr-FR')}</div>
                        <div className="text-gray-400">{new Date(log.created_at).toLocaleTimeString('fr-FR')}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 text-xs">{log.admin_name}</div>
                        <div className="text-gray-400 text-xs">{log.admin_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full font-mono ${actionBadge(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {log.target_type ? (
                          <span className="font-mono">
                            <span className="text-gray-500">{log.target_type}</span>
                            {log.target_id && <span className="text-gray-400"> #{log.target_id.slice(0, 8)}…</span>}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                        {log.ip_address || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {log.details && (
                          <button
                            onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                            className={`p-1 rounded transition-colors ${expanded === log.id ? 'text-red-500 bg-red-50' : 'text-gray-300 hover:text-gray-600'}`}
                            title="Voir les détails"
                          >
                            <FiInfo size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === log.id && log.details && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="px-4 pb-3 pt-0">
                          <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto mt-2">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {total > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {from}–{to} sur {total} entrée{total > 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 transition" title="Première page">
                  <FiChevronsLeft size={14} />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 transition">
                  <FiChevronLeft size={14} />
                </button>
                <span className="px-3 py-1 text-xs text-gray-600 border border-gray-200 rounded bg-white">
                  {page} / {totalPages || 1}
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 transition">
                  <FiChevronRight size={14} />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 transition" title="Dernière page">
                  <FiChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogs;
